import React, { useState } from 'react';
import GameRecap from './GameRecap';
import PostGameAnalysis from './PostGameAnalysis';
import MatchupPreviewModal from './matchup/MatchupPreviewModal';

const LineScore = ({ game }) => {
  const [showRecap, setShowRecap] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [showMatchup, setShowMatchup] = useState(false);

  const linescore = game?.linescore || {};
  const currentInnings = linescore.innings || [];
  const totalInnings = Math.max(9, currentInnings.length);
  const inningsArray = Array.from({ length: totalInnings }, (_, i) => i + 1);

  const awayTeam = game?.teams?.away;
  const homeTeam = game?.teams?.home;
  const isLive = game?.status?.abstractGameState === 'Live';
  const isFinal = game?.status?.abstractGameState === 'Final';
  const isPreview = game?.status?.abstractGameState === 'Preview';
  const currentInningNum = linescore.currentInning;
  const isTopInning = linescore.isTopInning;

  const getInningRuns = (teamType, inningIndex) => {
    const inning = currentInnings[inningIndex];
    if (!inning) return '';
    return inning[teamType]?.runs !== undefined ? inning[teamType].runs : '';
  };

  const TeamRow = ({ team, teamType, isBottomRow }) => (
    <tr className={!isBottomRow ? 'border-b border-[#658d71]' : ''}>
      <td className={`p-2 text-left font-bold text-sm sm:text-base uppercase tracking-wider ${isLive && ((teamType === 'away' && isTopInning) || (teamType === 'home' && !isTopInning)) ? 'text-white' : 'text-[#d8ceb3]'}`}>
        <div className="flex items-center gap-1.5">
          {team?.team?.abbreviation || '--'}
          {isLive && ((teamType === 'away' && isTopInning) || (teamType === 'home' && !isTopInning)) && (
            <span className="w-1.5 h-1.5 rounded-full bg-[#c4333e] animate-pulse" />
          )}
        </div>
      </td>
      {inningsArray.map((_, i) => {
        const inningNumber = i + 1;
        const isActiveHalf = isLive && inningNumber === currentInningNum &&
          ((teamType === 'away' && isTopInning) || (teamType === 'home' && !isTopInning));
        return (
          <td key={i} className={`p-1 border-l border-[#658d71] text-sm tabular-nums text-center ${isActiveHalf ? 'bg-[rgba(216,206,179,0.2)] text-[#d8ceb3] font-bold' : 'text-white'}`}>
            {getInningRuns(teamType, i)}
          </td>
        );
      })}
      <td className="p-1 border-l-2 border-[#8aa890] font-bold text-base tabular-nums text-white text-center bg-[rgba(0,0,0,0.1)]">
        {linescore.teams?.[teamType]?.runs ?? (game ? 0 : '--')}
      </td>
      <td className="p-1 border-l border-[#658d71] text-sm tabular-nums text-[#a9c9b1] text-center">
        {linescore.teams?.[teamType]?.hits ?? (game ? 0 : '--')}
      </td>
      <td className="p-1 border-l border-[#658d71] text-sm tabular-nums text-[#a9c9b1] text-center">
        {linescore.teams?.[teamType]?.errors ?? (game ? 0 : '--')}
      </td>
    </tr>
  );

  return (
    <>
      <div className="border-2 border-[#658d71] p-3 sm:p-6 bg-[rgba(0,0,0,0.05)] w-full">
        <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
          <h3 className="text-base sm:text-xl font-bold tracking-widest text-[#d8ceb3] uppercase">
            Line Score
          </h3>
          <div className="flex items-center gap-2 flex-wrap">
            {isLive && (
              <div className="flex items-center gap-2 text-[#a9c9b1] text-[10px] font-bold uppercase tracking-widest">
                <span className="w-2 h-2 rounded-full bg-[#c4333e] animate-pulse" />
                {isTopInning ? 'Top' : 'Bot'} {currentInningNum}
              </div>
            )}
            {game?.gamePk && (
              <div className="flex items-center gap-1.5">
                {(isPreview || (!isFinal && !isLive)) && (
                  <button
                    onClick={() => setShowMatchup(true)}
                    className="text-[9px] font-bold uppercase tracking-widest px-2.5 py-1 border border-[#658d71] text-[#a9c9b1] hover:border-[#a9c9b1] hover:text-white transition-colors"
                  >
                    Preview
                  </button>
                )}
                {isFinal && (
                  <button
                    onClick={() => setShowRecap(true)}
                    className="text-[9px] font-bold uppercase tracking-widest px-2.5 py-1 border border-[#658d71] text-[#a9c9b1] hover:border-[#d8ceb3] hover:text-[#d8ceb3] transition-colors"
                  >
                    Recap
                  </button>
                )}
                {(isFinal || isLive) && (
                  <button
                    onClick={() => setShowAnalysis(true)}
                    className="text-[9px] font-bold uppercase tracking-widest px-2.5 py-1 border border-[#c4333e] text-[#c4333e] hover:bg-[#c4333e] hover:text-white transition-colors"
                  >
                    {isLive ? 'Live Analysis' : 'Analysis'}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="overflow-x-auto w-full -mx-1">
          <table className="w-full text-center border-collapse" style={{ minWidth: '480px' }}>
            <thead>
              <tr className="border-b-2 border-[#8aa890] text-[#a9c9b1] text-[10px] sm:text-xs uppercase tracking-widest bg-[rgba(0,0,0,0.1)]">
                <th className="p-2 text-left font-medium w-16 sm:w-24">Team</th>
                {inningsArray.map(i => (
                  <th key={i} className={`p-1 w-7 border-l border-[#658d71] font-medium ${isLive && i === currentInningNum ? 'text-[#d8ceb3]' : ''}`}>
                    {i}
                  </th>
                ))}
                <th className="p-1 w-8 border-l-2 border-[#8aa890] font-bold text-[#d8ceb3]">R</th>
                <th className="p-1 w-8 border-l border-[#658d71] font-medium">H</th>
                <th className="p-1 w-8 border-l border-[#658d71] font-medium">E</th>
              </tr>
            </thead>
            <tbody>
              <TeamRow team={awayTeam} teamType="away" isBottomRow={false} />
              <TeamRow team={homeTeam} teamType="home" isBottomRow={true} />
            </tbody>
          </table>
        </div>

        {/* Inning Detail Row */}
        {(isFinal || isLive) && currentInnings.length > 0 && (
          <div className="mt-3 pt-3 border-t border-[rgba(101,141,113,0.3)] grid grid-cols-3 gap-2 text-center">
            {[
              { label: 'Total Runs', away: linescore.teams?.away?.runs ?? 0, home: linescore.teams?.home?.runs ?? 0 },
              { label: 'Total Hits', away: linescore.teams?.away?.hits ?? 0, home: linescore.teams?.home?.hits ?? 0 },
              { label: 'Errors', away: linescore.teams?.away?.errors ?? 0, home: linescore.teams?.home?.errors ?? 0 },
            ].map(({ label, away, home }) => (
              <div key={label} className="border border-[rgba(101,141,113,0.3)] p-2 bg-[rgba(0,0,0,0.08)] flex flex-col gap-1">
                <span className="text-[8px] uppercase tracking-widest text-[#658d71] font-bold">{label}</span>
                <div className="flex items-center justify-center gap-2">
                  <span className="text-[10px] font-black text-[#a9c9b1] tabular-nums">
                    {awayTeam?.team?.abbreviation || 'AWY'} {away}
                  </span>
                  <span className="text-[#658d71] text-[9px]">–</span>
                  <span className="text-[10px] font-black text-[#c4333e] tabular-nums">
                    {homeTeam?.team?.abbreviation || 'HME'} {home}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showRecap && <GameRecap game={game} onClose={() => setShowRecap(false)} />}
      {showAnalysis && <PostGameAnalysis game={game} onClose={() => setShowAnalysis(false)} />}
      {showMatchup && <MatchupPreviewModal game={game} onClose={() => setShowMatchup(false)} />}
    </>
  );
};

export default LineScore;